/** @module @liquidefy/code-editor/terminal/terminal-cloud @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  CloudTerminalState,
  CloudTerminalId,
  CloudProvider,
  CloudConnection,
  ContainerInfo,
  ResourceLimits,
  ScalingConfig,
  HealthCheckConfig,
  PortMapping,
  VolumeMount,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asCloudTerminalId,
  now,
  DEFAULT_CLOUD_RESOURCE_LIMITS,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CLOUD PROVISIONING — createCloudConfig, updateConnection, updateContainer, queries
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new cloud terminal configuration.
 */
export function createCloudConfig(
  id: string,
  provider: CloudProvider,
  limits?: Partial<ResourceLimits>
): EditorResult<CloudTerminalState> {
  const state: CloudTerminalState = {
    id: asCloudTerminalId(id),
    provider,
    connection: {
      endpoint: '',
      protocol: 'websocket',
      status: 'disconnected',
      latency: 0,
      encrypted: true,
    },
    resourceLimits: {
      ...DEFAULT_CLOUD_RESOURCE_LIMITS,
      ...limits,
    },
  };
  return ok(state, 'CLOUD_CREATED', `Cloud terminal created with provider ${provider}`);
}

/**
 * Update the connection status and latency.
 */
export function updateConnection(
  state: CloudTerminalState,
  status: CloudConnection['status'],
  latency?: number
): CloudTerminalState {
  return {
    ...state,
    connection: {
      ...state.connection,
      status,
      latency: latency ?? state.connection.latency,
    },
  };
}

/**
 * Update the container info.
 */
export function updateContainer(
  state: CloudTerminalState,
  info: Partial<ContainerInfo>
): CloudTerminalState {
  const current = state.container ?? {
    id: '',
    image: '',
    status: 'creating' as const,
    ports: [],
    volumes: [],
    env: {},
  };
  return {
    ...state,
    container: { ...current, ...info },
  };
}

/**
 * Check if the cloud terminal is connected.
 */
export function isConnected(state: CloudTerminalState): boolean {
  return state.connection.status === 'connected';
}

/**
 * Check if the cloud terminal is reconnecting.
 */
export function isReconnecting(state: CloudTerminalState): boolean {
  return state.connection.status === 'reconnecting';
}

/**
 * Compute the uptime of the current connection in milliseconds.
 */
export function connectionUptime(state: CloudTerminalState, currentTime: number): number {
  if (state.connection.status !== 'connected') return 0;
  // Uptime is tracked externally; we return a computed value based on current time
  // In the pure engine, we don't track connection start time in the type — IO manages this
  return currentTime > 0 ? currentTime : 0;
}

/**
 * Get the number of reconnection attempts.
 * In the pure model this is tracked by IO. Returns 0 as a placeholder.
 */
export function reconnectAttempts(_state: CloudTerminalState): number {
  return 0;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 CONTAINER MANAGEMENT — createContainerConfig, updateStatus, ports, volumes, label
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a container configuration.
 */
export function createContainerConfig(
  image: string,
  ports?: PortMapping[],
  volumes?: VolumeMount[]
): ContainerInfo {
  return {
    id: generateId('container'),
    image,
    status: 'creating',
    ports: ports ?? [],
    volumes: volumes ?? [],
    env: {},
  };
}

/**
 * Update the container status.
 */
export function updateContainerStatus(
  container: ContainerInfo,
  status: ContainerInfo['status']
): ContainerInfo {
  return { ...container, status };
}

/**
 * Add a port mapping to a container.
 */
export function addPortMapping(
  container: ContainerInfo,
  containerPort: number,
  hostPort: number,
  protocol?: 'tcp' | 'udp'
): ContainerInfo {
  const mapping: PortMapping = {
    containerPort,
    hostPort,
    protocol: protocol ?? 'tcp',
  };
  return { ...container, ports: [...container.ports, mapping] };
}

/**
 * Remove a port mapping by container port.
 */
export function removePortMapping(
  container: ContainerInfo,
  port: number
): ContainerInfo {
  return {
    ...container,
    ports: container.ports.filter(p => p.containerPort !== port),
  };
}

/**
 * Add a volume mount to a container.
 */
export function addVolumeMount(
  container: ContainerInfo,
  source: string,
  target: string,
  readOnly?: boolean
): ContainerInfo {
  const mount: VolumeMount = {
    source,
    target,
    readOnly: readOnly ?? false,
  };
  return { ...container, volumes: [...container.volumes, mount] };
}

/**
 * Remove a volume mount by target path.
 */
export function removeVolumeMount(
  container: ContainerInfo,
  target: string
): ContainerInfo {
  return {
    ...container,
    volumes: container.volumes.filter(v => v.target !== target),
  };
}

/**
 * Generate a human-readable label for a container.
 */
export function containerLabel(container: ContainerInfo): string {
  const portStr = container.ports.length > 0
    ? ` [${container.ports.map(p => `${p.containerPort}:${p.hostPort}`).join(', ')}]`
    : '';
  return `${container.image} (${container.status})${portStr}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 RESOURCE MANAGEMENT — validateLimits, merge, isWithin, utilization, cost, defaults
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Validate resource limits.
 */
export function validateLimits(limits: ResourceLimits): EditorResult<ResourceLimits> {
  if (limits.cpuCores <= 0) {
    return err('INVALID_CPU', `CPU cores must be positive, got ${limits.cpuCores}`);
  }
  if (limits.memoryMb <= 0) {
    return err('INVALID_MEMORY', `Memory must be positive, got ${limits.memoryMb}`);
  }
  if (limits.storageMb <= 0) {
    return err('INVALID_STORAGE', `Storage must be positive, got ${limits.storageMb}`);
  }
  if (limits.networkMbps !== undefined && limits.networkMbps < 0) {
    return err('INVALID_NETWORK', `Network bandwidth must be non-negative, got ${limits.networkMbps}`);
  }
  return ok(limits, 'LIMITS_VALID', 'Resource limits are valid');
}

/**
 * Merge resource limits with overrides.
 */
export function mergeResourceLimits(
  base: ResourceLimits,
  overrides: Partial<ResourceLimits>
): ResourceLimits {
  return {
    cpuCores: overrides.cpuCores ?? base.cpuCores,
    memoryMb: overrides.memoryMb ?? base.memoryMb,
    storageMb: overrides.storageMb ?? base.storageMb,
    networkMbps: overrides.networkMbps ?? base.networkMbps,
  };
}

/**
 * Check if current resource usage is within limits.
 */
export function isWithinLimits(
  current: { cpuCores: number; memoryMb: number; storageMb: number },
  limits: ResourceLimits
): boolean {
  return (
    current.cpuCores <= limits.cpuCores &&
    current.memoryMb <= limits.memoryMb &&
    current.storageMb <= limits.storageMb
  );
}

/**
 * Compute the utilization percentage for each resource.
 */
export function utilizationPercentage(
  current: { cpuCores: number; memoryMb: number; storageMb: number },
  limits: ResourceLimits
): { cpu: number; memory: number; storage: number } {
  return {
    cpu: limits.cpuCores > 0 ? (current.cpuCores / limits.cpuCores) * 100 : 0,
    memory: limits.memoryMb > 0 ? (current.memoryMb / limits.memoryMb) * 100 : 0,
    storage: limits.storageMb > 0 ? (current.storageMb / limits.storageMb) * 100 : 0,
  };
}

/**
 * Estimate the cost of running a cloud terminal for a given duration.
 * Returns cost in cents.
 */
export function estimateCost(
  provider: CloudProvider,
  limits: ResourceLimits,
  durationMs: number
): number {
  const rates = PROVIDER_PRICING[provider];
  const hours = durationMs / (1000 * 60 * 60);

  const cpuCost = limits.cpuCores * rates.cpuPerCoreHour * hours;
  const memCost = (limits.memoryMb / 1024) * rates.memoryPerGbHour * hours;
  const storageCost = (limits.storageMb / 1024) * rates.storagePerGbMonth / 720 * hours;

  return Math.round((cpuCost + memCost + storageCost) * 100); // cents
}

/**
 * Simplified per-provider pricing (dollars per unit per hour).
 */
export const PROVIDER_PRICING: Record<CloudProvider, {
  cpuPerCoreHour: number;
  memoryPerGbHour: number;
  storagePerGbMonth: number;
}> = {
  'aws': { cpuPerCoreHour: 0.0425, memoryPerGbHour: 0.0053, storagePerGbMonth: 0.10 },
  'gcp': { cpuPerCoreHour: 0.0380, memoryPerGbHour: 0.0051, storagePerGbMonth: 0.08 },
  'azure': { cpuPerCoreHour: 0.0440, memoryPerGbHour: 0.0055, storagePerGbMonth: 0.12 },
  'digital-ocean': { cpuPerCoreHour: 0.0300, memoryPerGbHour: 0.0045, storagePerGbMonth: 0.10 },
  'kubernetes': { cpuPerCoreHour: 0.0350, memoryPerGbHour: 0.0050, storagePerGbMonth: 0.08 },
  'local-docker': { cpuPerCoreHour: 0, memoryPerGbHour: 0, storagePerGbMonth: 0 },
};

/**
 * Default resource limits per provider.
 */
export const PROVIDER_DEFAULTS: Record<CloudProvider, ResourceLimits> = {
  'aws': { cpuCores: 2, memoryMb: 4096, storageMb: 20480, networkMbps: 1000 },
  'gcp': { cpuCores: 2, memoryMb: 4096, storageMb: 20480, networkMbps: 1000 },
  'azure': { cpuCores: 2, memoryMb: 4096, storageMb: 20480, networkMbps: 1000 },
  'digital-ocean': { cpuCores: 1, memoryMb: 2048, storageMb: 25600, networkMbps: 1000 },
  'kubernetes': { cpuCores: 1, memoryMb: 2048, storageMb: 10240, networkMbps: 500 },
  'local-docker': { cpuCores: 2, memoryMb: 4096, storageMb: 51200 },
};

// ═══════════════════════════════════════════════════════════════════════════════
// §5 SCALING — configureScaling, shouldScaleUp, shouldScaleDown, computeReplicaCount, cooldown
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a scaling configuration with defaults.
 */
export function configureScaling(config?: Partial<ScalingConfig>): ScalingConfig {
  return {
    enabled: config?.enabled ?? false,
    minReplicas: config?.minReplicas ?? 1,
    maxReplicas: config?.maxReplicas ?? 5,
    cpuThreshold: config?.cpuThreshold ?? 80,
    memoryThreshold: config?.memoryThreshold ?? 85,
  };
}

/**
 * Determine if scaling up is needed based on metrics.
 */
export function shouldScaleUp(
  metrics: { cpuPercent: number; memoryPercent: number; currentReplicas: number },
  config: ScalingConfig
): boolean {
  if (!config.enabled) return false;
  if (metrics.currentReplicas >= config.maxReplicas) return false;
  return metrics.cpuPercent > config.cpuThreshold || metrics.memoryPercent > config.memoryThreshold;
}

/**
 * Determine if scaling down is possible based on metrics.
 */
export function shouldScaleDown(
  metrics: { cpuPercent: number; memoryPercent: number; currentReplicas: number },
  config: ScalingConfig
): boolean {
  if (!config.enabled) return false;
  if (metrics.currentReplicas <= config.minReplicas) return false;
  // Scale down if both CPU and memory are well below thresholds
  const cpuLow = metrics.cpuPercent < config.cpuThreshold * 0.5;
  const memLow = metrics.memoryPercent < config.memoryThreshold * 0.5;
  return cpuLow && memLow;
}

/**
 * Compute the desired replica count based on resource usage.
 */
export function computeReplicaCount(
  cpuUsage: number,
  memoryUsage: number,
  config: ScalingConfig
): number {
  if (!config.enabled) return config.minReplicas;

  const cpuRatio = cpuUsage / config.cpuThreshold;
  const memRatio = memoryUsage / config.memoryThreshold;
  const maxRatio = Math.max(cpuRatio, memRatio);

  // Scale proportionally to the highest utilization ratio
  const desired = Math.ceil(maxRatio);
  return Math.max(config.minReplicas, Math.min(config.maxReplicas, desired));
}

/**
 * Check if the scaling cooldown period has elapsed.
 */
export function scalingCooldown(
  lastScaleTime: number,
  cooldownMs: number
): boolean {
  return (now() - lastScaleTime) >= cooldownMs;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 HEALTH CHECK — createHealthCheck, isHealthy, healthCheckDue, failures, shouldRestart, healthStatus
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a health check configuration.
 */
export function createHealthCheck(
  command: string,
  interval?: number,
  timeout?: number
): HealthCheckConfig {
  return {
    command,
    interval: interval ?? 30000,
    timeout: timeout ?? 5000,
    retries: 3,
    startPeriod: 10000,
  };
}

/**
 * Check if all recent health check results indicate healthy.
 */
export function isHealthy(checks: Array<{ success: boolean; timestamp: number }>): boolean {
  if (checks.length === 0) return true; // No checks = assume healthy
  // Healthy if the most recent check passed
  return checks[checks.length - 1]!.success;
}

/**
 * Check if a health check is due based on the last check time and interval.
 */
export function healthCheckDue(lastCheck: number, interval: number): boolean {
  return (now() - lastCheck) >= interval;
}

/**
 * Count consecutive failures from the end of the results array.
 */
export function consecutiveFailures(
  results: Array<{ success: boolean; timestamp: number }>
): number {
  let count = 0;
  for (let i = results.length - 1; i >= 0; i--) {
    if (!results[i]!.success) {
      count++;
    } else {
      break;
    }
  }
  return count;
}

/**
 * Determine if a restart should be triggered based on failure count.
 */
export function shouldRestart(failures: number, maxRetries: number): boolean {
  return failures >= maxRetries;
}

/**
 * Compute overall health status from recent check results.
 */
export function healthStatus(
  results: Array<{ success: boolean; timestamp: number }>
): 'healthy' | 'degraded' | 'unhealthy' {
  if (results.length === 0) return 'healthy';

  const recent = results.slice(-5);
  const failures = recent.filter(r => !r.success).length;

  if (failures === 0) return 'healthy';
  if (failures >= recent.length) return 'unhealthy';
  return 'degraded';
}
